import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetOrdersQuery } from '../../../store/api/ordersApi';
import type { Order } from '../../../types/api';
import { formatAmountExact } from '../../../utils/amountFormatter';
import { formatDate } from '../../../utils/dateFormatter';

export default function OrdersListPage() {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';
  const [searchParams] = useSearchParams();
  const userIdParam = searchParams.get('userId');

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [userId, setUserId] = useState(userIdParam ? parseInt(userIdParam, 10) : undefined);
  const [status, setStatus] = useState<'new' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded' | null>(null);
  const [section, setSection] = useState<'social_ar' | 'social_en' | 'games' | 'manual' | null>(null);
  const [providerId, setProviderId] = useState('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, error } = useGetOrdersQuery({
    page,
    limit,
    userId: userId || undefined,
    status: status || undefined,
    section: section || undefined,
    providerId: providerId || undefined,
    sortBy,
    sortOrder,
  });

  const orders = data?.data?.orders || [];
  const pagination = data?.data?.pagination;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const clearFilters = () => {
    setUserId(undefined);
    setStatus(null);
    setSection(null);
    setProviderId('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(1);
  };

  const getStatusColor = (orderStatus: string) => {
    switch (orderStatus) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'processing':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'failed':
      case 'cancelled':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'refunded':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default:
        return 'bg-neutral-medium/20 text-neutral-medium border-neutral-medium/50';
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">
          {t('admin.orders.error.title') || 'Error loading orders'}
        </p>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header with animation */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent">
            {t('admin.orders.title') || 'Orders Management'}
          </h1>
          {pagination && (
            <div className="bg-brand-accent/20 border border-brand-accent/50 rounded-lg px-3 sm:px-4 py-2 w-fit">
              <span className="text-xs sm:text-sm font-medium text-neutral-medium">
                {t('admin.orders.total') || 'Total Orders'}:{' '}
              </span>
              <span className="text-base sm:text-lg font-heading font-bold text-brand-accent">
                {pagination.total}
              </span>
            </div>
          )}
        </div>
        <p className="text-sm sm:text-base text-neutral-medium">
          {t('admin.orders.subtitle') || 'Manage all orders in the system'}
        </p>
      </div>

      {/* Filters with animation */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50 mb-4 sm:mb-6 transition-all duration-300 hover:border-brand-accent/30"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* User ID Search */}
            <div
              className="sm:col-span-2 lg:col-span-1"
              style={{
                animation: 'fadeIn 0.5s ease-out 0.2s both',
              }}
            >
              <label htmlFor="userId" className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.orders.filters.user') || 'User ID'}
              </label>
              <input
                id="userId"
                type="number"
                value={userId || ''}
                onChange={(e) => setUserId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                placeholder={t('admin.orders.filters.userPlaceholder') || 'Enter User ID...'}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              />
            </div>

            {/* Status Filter */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.3s both',
              }}
            >
              <label htmlFor="status" className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.orders.filters.status') || 'Status'}
              </label>
              <select
                id="status"
                value={status || 'all'}
                onChange={(e) => {
                  const value = e.target.value;
                  setStatus(value === 'all' ? null : (value as typeof status));
                }}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              >
                <option value="all">{t('admin.orders.filters.all') || 'All'}</option>
                <option value="new">{t('admin.orders.status.new') || 'New'}</option>
                <option value="processing">{t('admin.orders.status.processing') || 'Processing'}</option>
                <option value="completed">{t('admin.orders.status.completed') || 'Completed'}</option>
                <option value="failed">{t('admin.orders.status.failed') || 'Failed'}</option>
                <option value="cancelled">{t('admin.orders.status.cancelled') || 'Cancelled'}</option>
                <option value="refunded">{t('admin.orders.status.refunded') || 'Refunded'}</option>
              </select>
            </div>

            {/* Section Filter */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.4s both',
              }}
            >
              <label htmlFor="section" className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.orders.filters.section') || 'Section'}
              </label>
              <select
                id="section"
                value={section || 'all'}
                onChange={(e) => {
                  const value = e.target.value;
                  setSection(value === 'all' ? null : (value as typeof section));
                }}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              >
                <option value="all">{t('admin.orders.filters.all') || 'All'}</option>
                <option value="social_ar">{t('admin.orders.filters.socialAr') || 'Social AR'}</option>
                <option value="social_en">{t('admin.orders.filters.socialEn') || 'Social EN'}</option>
                <option value="games">{t('admin.orders.filters.games') || 'Games'}</option>
                <option value="manual">{t('admin.orders.filters.manual') || 'Manual'}</option>
              </select>
            </div>

            {/* Provider Filter */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.5s both',
              }}
            >
              <label htmlFor="providerId" className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.orders.filters.provider') || 'Provider'}
              </label>
              <input
                id="providerId"
                type="text"
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                placeholder={t('admin.orders.filters.providerPlaceholder') || 'Provider ID...'}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              />
            </div>
          </div>

          <div
            className="flex flex-col sm:flex-row gap-3"
            style={{
              animation: 'fadeIn 0.5s ease-out 0.6s both',
            }}
          >
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm sm:text-base"
            >
              {t('common.search') || 'Search'}
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="w-full sm:w-auto px-6 py-2 bg-neutral-medium text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 hover:scale-105 active:scale-95 text-sm sm:text-base"
            >
              {t('admin.orders.filters.clear') || 'Clear Filters'}
            </button>
          </div>
        </form>
      </div>

      {/* Orders Table - Desktop */}
      {orders.length === 0 ? (
        <div
          className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-8 sm:p-12 border border-neutral-dark/50 text-center"
          style={{
            animation: 'fadeIn 0.5s ease-out 0.3s both',
          }}
        >
          <p className="text-base sm:text-lg text-neutral-medium">
            {t('admin.orders.empty') || 'No orders found'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div
            className="hidden lg:block bg-neutral-dark/50 backdrop-blur-lg rounded-lg border border-neutral-dark/50 overflow-hidden transition-all duration-300 hover:border-brand-accent/30"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-dark/80 border-b border-neutral-medium">
                  <tr>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.orders.table.orderId') || 'Order ID'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.orders.table.user') || 'User'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.orders.table.service') || 'Service'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.orders.table.quantity') || 'Quantity'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.orders.table.price') || 'Price'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.orders.table.status') || 'Status'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.orders.table.created') || 'Created'}
                    </th>
                    <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                      {t('admin.orders.table.actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-medium">
                  {orders.map((order: Order, index: number) => (
                    <tr
                      key={order._id}
                      className="hover:bg-neutral-dark/50 transition-all duration-200 hover:shadow-lg hover:shadow-brand-accent/5"
                      style={{
                        animation: `fadeIn 0.4s ease-out ${index * 30}ms both`,
                      }}
                    >
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-light">
                        <span className="font-mono text-xs xl:text-sm">{order._id.slice(-8)}</span>
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-light">
                        <Link
                          to={`/admin/users/${order.userId}`}
                          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200 hover:underline text-sm xl:text-base"
                        >
                          {order.userId}
                        </Link>
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-light">
                        <div className="flex flex-col">
                          <span className="font-medium text-sm xl:text-base">{order.service}</span>
                          <span className="text-xs text-neutral-medium capitalize">{order.section}</span>
                        </div>
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-light text-sm xl:text-base">
                        {order.quantity}
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-brand-luxury font-semibold text-sm xl:text-base drop-shadow-[0_0_2px_rgba(230,161,87,0.15)]">
                        ${formatAmountExact(order.finalPrice ?? 0)}
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded border ${getStatusColor(order.status)}`}>
                          {t(`admin.orders.status.${order.status}`) || order.status}
                        </span>
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap text-neutral-medium text-xs xl:text-sm">
                        {formatDate(order.createdAt, 'PP', currentLanguage)}
                      </td>
                      <td className="px-4 xl:px-6 py-3 xl:py-4 whitespace-nowrap">
                        <Link
                          to={`/admin/orders/${order._id}`}
                          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline inline-flex items-center gap-1 text-sm xl:text-base"
                        >
                          {t('admin.orders.table.view') || 'View'}
                          <svg
                            className="w-3 h-3 xl:w-4 xl:h-4 inline-block transition-transform duration-200 group-hover:translate-x-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div
            className="lg:hidden space-y-3 sm:space-y-4"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            {orders.map((order: Order, index: number) => (
              <div
                key={order._id}
                className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-5 border border-neutral-dark/50 hover:border-brand-accent/50 transition-all duration-200"
                style={{
                  animation: `fadeIn 0.4s ease-out ${index * 50}ms both`,
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-base sm:text-lg font-heading font-semibold text-neutral-light">
                        {order.service}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${getStatusColor(order.status)}`}>
                        {t(`admin.orders.status.${order.status}`) || order.status}
                      </span>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.orders.table.orderId') || 'Order ID'}:
                        </span>
                        <span className="text-neutral-light font-mono text-xs">{order._id.slice(-8)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.orders.table.user') || 'User'}:
                        </span>
                        <Link
                          to={`/admin/users/${order.userId}`}
                          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200 hover:underline"
                        >
                          {order.userId}
                        </Link>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.orders.table.quantity') || 'Quantity'}:
                        </span>
                        <span className="text-neutral-light">{order.quantity}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.orders.table.price') || 'Price'}:
                        </span>
                        <span className="text-brand-accent font-semibold">${formatAmountExact(order.finalPrice ?? 0)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.orders.table.section') || 'Section'}:
                        </span>
                        <span className="text-neutral-light capitalize">{order.section}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-neutral-medium font-medium">
                          {t('admin.orders.table.created') || 'Created'}:
                        </span>
                        <span className="text-neutral-light text-xs">
                          {formatDate(order.createdAt, 'PP', currentLanguage)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <Link
                  to={`/admin/orders/${order._id}`}
                  className="block w-full mt-3 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 text-center text-sm"
                >
                  {t('admin.orders.table.view') || 'View Details'}
                </Link>
              </div>
            ))}
          </div>

          {/* Pagination with animation */}
          {pagination && pagination.pages > 1 && (
            <div
              className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0"
              style={{
                animation: 'fadeIn 0.5s ease-out 0.4s both',
              }}
            >
              <div className="text-neutral-medium text-xs sm:text-sm text-center sm:text-left">
                {t('admin.orders.pagination.showing') || 'Showing'}{' '}
                <span className="text-brand-accent font-semibold">
                  {(pagination.page - 1) * pagination.limit + 1}
                </span>{' '}
                -{' '}
                <span className="text-brand-accent font-semibold">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                {t('admin.orders.pagination.of') || 'of'}{' '}
                <span className="text-brand-accent font-semibold">{pagination.total}</span>{' '}
                {t('admin.orders.pagination.orders') || 'orders'}
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page === 1}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light hover:bg-neutral-medium/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100 text-sm sm:text-base"
                >
                  {t('admin.orders.pagination.previous') || 'Previous'}
                </button>
                <span className="hidden sm:inline-flex items-center px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light text-sm sm:text-base">
                  {t('admin.orders.pagination.page') || 'Page'}{' '}
                  <span className="text-brand-accent font-semibold mx-1">{pagination.page}</span>{' '}
                  {t('admin.orders.pagination.of') || 'of'}{' '}
                  <span className="text-brand-accent font-semibold mx-1">{pagination.pages}</span>
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={pagination.page === pagination.pages}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light hover:bg-neutral-medium/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100 text-sm sm:text-base"
                >
                  {t('admin.orders.pagination.next') || 'Next'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
